import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { UniversalSearchBar } from '../../shared/ui/UniversalSearchBar';
import { HeaderCompanySelector } from '../../shared/ui/HeaderCompanySelector';
import { theme, getTenant } from '../../theme/tokens';
import { BarChartIcon, CreditCardIcon, QrCodeIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon, MegaphoneIcon, HomeIcon, BedDoubleIcon, ClipboardListIcon, DollarSignIcon, BookOpenIcon, TruckIcon, FileIcon, AlertTriangleIcon, RefreshCwIcon, ShieldIcon, BarChart3Icon, HelpCircleIcon, BellIcon, PlusIcon, LinkIcon, RepeatIcon, MoreHorizontalIcon, ArrowUpDownIcon, CodeIcon, WebhookIcon, ActivityIcon, ClockIcon } from 'lucide-react';
const {
  colors,
  brand,
  layout
} = theme;
const {
  sidebar: sidebarColors,
  header: headerColors,
  assistant: assistantColors
} = colors;
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const {
    t
  } = useTranslation('common');
  const [menuOpen, setMenuOpen] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const [reportingExpanded, setReportingExpanded] = useState(false);
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [connectExpanded, setConnectExpanded] = useState(false);
  const [webhooksExpanded, setWebhooksExpanded] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationTab, setNotificationTab] = useState<'unread' | 'all'>('unread');
  const notificationsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout
  } = useAuth();
  const placeholderTexts = ['How do I activate new payment methods?', 'How long does it take to receive a verdict on a dispute?', 'Can I refund a successful transaction from the past week?', "How long can I place a temporary hold on a customer's card for?"];
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholderTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const isActive = (path: string) => {
    if (path === '/overview') {
      return location.pathname === '/overview' || location.pathname === '/';
    }
    return location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return location.pathname === '/payment-requests';
  };
  const isBalanceActive = () => {
    return ['/balance', '/balance-activity', '/payouts'].some(path => location.pathname.startsWith(path));
  };
  const isTransactionsActive = () => {
    return ['/sales', '/disputes', '/refunds', '/payment-analytics'].some(path => location.pathname.startsWith(path));
  };
  const isReportingActive = () => {
    return ['/reports', '/custom-metrics', '/nadi'].some(path => location.pathname.startsWith(path));
  };
  const getPageTitle = () => {
    if (location.pathname === '/overview' || location.pathname === '/') return 'Overview';
    if (location.pathname === '/payment-requests') return 'Payment Requests';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/webhooks') return 'Webhooks';
    if (location.pathname === '/webhooks/endpoints') return 'Webhook Endpoints';
    if (location.pathname === '/webhooks/logs') return 'Webhook Logs';
    if (location.pathname === '/orders') return 'Orders';
    if (location.pathname === '/tutorials') return 'Tutorials';
    if (location.pathname === '/campaigns') return 'Campaigns';
    if (location.pathname === '/campaign-details') return 'Campaign Details';
    if (location.pathname === '/campaign-form') return location.search.includes('id=') ? 'Update Campaign' : 'Create Campaign';
    if (location.pathname === '/invoices') return 'Invoices';
    if (location.pathname.startsWith('/invoice-detail/')) return 'Invoice Details';
    if (location.pathname === '/invoice-form') return location.search.includes('id=') ? 'Edit Invoice' : 'Create Invoice';
    if (location.pathname === '/customers') return 'Customers';
    if (location.pathname.startsWith('/customer-detail/')) return 'Customer Details';
    if (location.pathname === '/customer-form') return location.search.includes('id=') ? 'Edit Customer' : 'Create Customer';
    if (location.pathname === '/sales') return 'Sales';
    if (location.pathname.startsWith('/sale-detail/')) return 'Sale Details';
    if (location.pathname === '/disputes') return 'Disputes';
    if (location.pathname.startsWith('/dispute-detail/')) return 'Dispute Details';
    if (location.pathname === '/refunds') return 'Refunds';
    if (location.pathname.startsWith('/refund-detail/')) return 'Refund Details';
    if (location.pathname === '/suppliers') return 'Suppliers';
    if (location.pathname.startsWith('/supplier-detail/')) return 'Supplier Details';
    if (location.pathname === '/supplier-form') return location.search.includes('id=') ? 'Edit Supplier' : 'Create Supplier';
    if (location.pathname === '/supplier-bulk-upload') return 'Bulk Upload Suppliers';
    if (location.pathname === '/bills') return 'Bills';
    if (location.pathname.startsWith('/bill-detail/')) return 'Bill Details';
    if (location.pathname === '/bill-form') return location.search.includes('id=') ? 'Edit Bill' : 'Create Bill';
    if (location.pathname.startsWith('/bill-approval/')) return 'Bill Approval';
    if (location.pathname.startsWith('/bill-financing/')) return 'Request Bill Financing';
    if (location.pathname.startsWith('/tutorial/')) return 'Tutorial';
    if (location.pathname === '/reports') return 'Reports';
    if (location.pathname === '/custom-metrics') return 'Custom Metrics';
    if (location.pathname === '/nadi') return 'Nadi';
    if (location.pathname === '/activity-logs') return 'Activity Logs';
    if (location.pathname === '/requests') return 'Requests';
    if (location.pathname.startsWith('/requests/')) return 'Request Details';
    return 'Dashboard';
  };
  // Nav item styles using theme values
  const navItemStyle = (active: boolean): React.CSSProperties => ({
    color: active ? sidebarColors.text.active : sidebarColors.text.default,
    fontWeight: active ? 500 : 400
  });
  const navItemHoverHandlers = (active: boolean) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.hover;
        e.currentTarget.style.backgroundColor = sidebarColors.hoverBg;
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.default;
        e.currentTarget.style.backgroundColor = 'transparent';
      }
    }
  });
  const subNavItemStyle = (active: boolean): React.CSSProperties => ({
    color: active ? sidebarColors.text.active : sidebarColors.text.subDefault,
    fontWeight: active ? 500 : 400
  });
  const subNavHoverHandlers = (active: boolean) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.hover;
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!active) {
        e.currentTarget.style.color = sidebarColors.text.subDefault;
      }
    }
  });
  // Close notifications on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close any open dropdown / menu / mobile nav on Escape (WCAG 2.1.2 / 2.1.1)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (notificationsOpen) {
        setNotificationsOpen(false);
        return;
      }
      if (userMenuOpen) {
        setUserMenuOpen(false);
        return;
      }
      if (createMenuOpen) {
        setCreateMenuOpen(false);
        return;
      }
      if (menuOpen) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [notificationsOpen, userMenuOpen, createMenuOpen, menuOpen]);
  const notifications = [{
    id: 1,
    message: `Dispute #DP-2024-0001 requires your response. ${getTenant().currency.code} 100.01 fraudulent charge — deadline approaching.`,
    date: 'Mar 15, 2026',
    unread: true,
    type: 'urgent'
  }, {
    id: 2,
    message: `Dispute #DP-2024-0091 requires response. ${getTenant().currency.code} 1,538.94 duplicate charge — due in 5 hours.`,
    date: 'Mar 15, 2026',
    unread: true,
    type: 'urgent'
  }, {
    id: 3,
    message: 'Bill from Acme Corp (INV-2026-003) is pending your approval. Due Mar 20, 2026.',
    date: 'Mar 14, 2026',
    unread: true,
    type: 'bill'
  }, {
    id: 4,
    message: `Payment of ${getTenant().currency.code} 45,000 received from John Doe. Transaction completed successfully.`,
    date: 'Mar 14, 2026',
    unread: true,
    type: 'payment'
  }, {
    id: 5,
    message: `New refund processed for ${getTenant().currency.code} 2,500. Reason: Customer request. Refund ID #RF-0042.`,
    date: 'Mar 13, 2026',
    unread: true,
    type: 'refund'
  }, {
    id: 6,
    message: 'Settlement completed successfully at 01:30 AM. All funds have been transferred.',
    date: 'Mar 13, 2026',
    unread: false,
    type: 'system'
  }, {
    id: 7,
    message: 'API uptime report: All systems operational. 99.99% uptime this month.',
    date: 'Mar 12, 2026',
    unread: false,
    type: 'system'
  }, {
    id: 8,
    message: 'New payment method integration available: Cryptocurrency payments now supported.',
    date: 'Mar 10, 2026',
    unread: false,
    type: 'system'
  }];
  const unreadNotifications = notifications.filter(n => n.unread);
  const displayNotifications = notificationTab === 'unread' ? unreadNotifications : notifications;
  return <div className="min-h-screen" style={{
    backgroundColor: colors.background.page
  }} data-id="element-4">
      {/* Skip to main content link (WCAG 2.4.1) */}
      <a href="#main-content" className="skip-to-content" data-id="element-5">
        Skip to main content
      </a>

      {/* Top Header Bar - spans FULL width */}
      <div className="fixed top-0 left-0 right-0 z-30" style={{
      backgroundColor: headerColors.bg,
      height: layout.header.height
    }} data-id="element-6">
        <div className="flex items-center h-full" data-id="element-7">
          {/* Logo section - exactly matches sidebar width */}
          <div className="hidden lg:flex items-center justify-center flex-shrink-0 h-full" style={{
          width: layout.sidebar.width,
          backgroundColor: '#0066b3',
          borderBottom: `1px solid #0066b3`
        }} data-id="element-8">
            <img src={getTenant().brand.logo?.dark || getTenant().brand.logo?.light} alt={getTenant().brand.name} className="h-12 w-auto object-contain" data-id="element-9" />
          </div>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center px-5 flex-shrink-0 h-full" style={{
          backgroundColor: '#0066b3',
          borderBottom: `1px solid #0066b3`
        }} data-id="element-10">
            <img src={getTenant().brand.logo?.dark || getTenant().brand.logo?.light} alt={getTenant().brand.name} className="h-11 w-auto object-contain" data-id="element-11" />
          </div>

          {/* Separator + hamburger + search + right icons */}
          <div className="flex items-center flex-1 h-full px-5 gap-5" style={{
          borderLeft: `1px solid ${headerColors.border}`,
          borderBottom: `1px solid ${headerColors.border}`
        }} data-id="element-12">
            {/* Hamburger menu - mobile only */}
            <button type="button" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={menuOpen} aria-controls="primary-navigation" className="lg:hidden p-1.5 flex-shrink-0 transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500" style={{
            color: colors.neutral[400]
          }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[600]} onMouseLeave={e => e.currentTarget.style.color = colors.neutral[400]} data-id="element-13">
              <MenuIcon size={20} aria-hidden="true" data-id="element-14" />
            </button>

            {/* Company/Branch Selector */}
            <div className="min-w-0 flex-shrink sm:flex-shrink-0" data-id="element-15">
              <HeaderCompanySelector data-id="element-16" />
            </div>

            {/* Search Bar - hidden on small mobile to give selector room */}
            <div className="hidden md:block flex-1 max-w-2xl" data-id="element-17">
              <UniversalSearchBar data-id="element-18" />
            </div>

            {/* Right side */}
            <div className="flex items-center gap-1 ml-auto" data-id="element-19">
              <button type="button" onClick={() => setAssistantOpen(!assistantOpen)} aria-label={t('app.AppLayout.help') as string} aria-expanded={assistantOpen} className="p-2 rounded-md hover:bg-gray-50 transition-colors relative group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-20">
                <HelpCircleIcon size={18} aria-hidden="true" style={{
                color: headerColors.iconDefault
              }} data-id="element-21" />

                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" aria-hidden="true" data-id="element-22">
                  {t('app.AppLayout.help')}
                </span>
              </button>

              <div className="relative" ref={notificationsRef} data-id="element-23">
                <button type="button" onClick={() => setNotificationsOpen(!notificationsOpen)} aria-label={`${t('app.AppLayout.notifications')}${unreadNotifications.length > 0 ? `, ${unreadNotifications.length} unread` : ''}`} aria-expanded={notificationsOpen} aria-haspopup="dialog" className="p-2 rounded-md hover:bg-gray-50 transition-colors relative group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-24">
                  <BellIcon size={18} aria-hidden="true" style={{
                  color: headerColors.iconDefault
                }} data-id="element-25" />

                  {unreadNotifications.length > 0 && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" aria-hidden="true" style={{
                  backgroundColor: headerColors.notificationDot
                }} data-id="element-26"></span>}
                  {!notificationsOpen && <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" aria-hidden="true" data-id="element-27">
                      {t('app.AppLayout.notifications')}
                    </span>}
                </button>

                {notificationsOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setNotificationsOpen(false)} data-id="element-28" />

                    <div role="dialog" aria-label={t('app.AppLayout.notifications') as string} className="absolute right-0 mt-2 w-[420px] bg-white rounded-xl shadow-xl border border-gray-200 z-20 overflow-hidden" data-id="element-29">
                      {/* Header */}
                      <div className="flex items-center justify-between px-5 pt-4 pb-3" data-id="element-30">
                        <h3 className="text-base font-semibold text-gray-900" data-id="element-31">
                          {t('app.AppLayout.notifications')}
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-32">
                          {unreadNotifications.length > 0 && <button className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md transition-colors" data-id="element-33">
                              {t('app.AppLayout.mark')}{' '}
                              {unreadNotifications.length}{' '}
                              {t('app.AppLayout.as_read')}
                            </button>}
                          <button onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/settings');
                      }} className="text-xs font-medium text-gray-600 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors" data-id="element-34">
                            {t('app.AppLayout.view_preferences')}
                          </button>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="flex px-5 gap-2 mb-1" data-id="element-35">
                        <button onClick={() => setNotificationTab('unread')} className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${notificationTab === 'unread' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-36">
                          {t('app.AppLayout.unread')}
                        </button>
                        <button onClick={() => setNotificationTab('all')} className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${notificationTab === 'all' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-37">
                          {t('app.AppLayout.all')}
                        </button>
                      </div>

                      {/* Notification List */}
                      <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-100" data-id="element-38">
                        {displayNotifications.length === 0 ? <div className="px-5 py-8 text-center" data-id="element-39">
                            <p className="text-sm text-gray-500" data-id="element-40">
                              {t('app.AppLayout.no_notifications')}
                            </p>
                          </div> : displayNotifications.slice(0, 5).map(notification => <div key={notification.id} className="px-5 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-41">
                                <div className="flex items-start gap-2" data-id="element-42">
                                  {notification.unread && <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 flex-shrink-0" data-id="element-43"></span>}
                                  <div className={`flex-1 min-w-0 ${!notification.unread ? 'ml-4' : ''}`} data-id="element-44">
                                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-45">
                                      {notification.message}
                                    </p>
                                    <div className="flex items-center justify-between mt-1.5" data-id="element-46">
                                      <span className="text-xs text-gray-400" data-id="element-47">
                                        {notification.date}
                                      </span>
                                      <button className="p-1 rounded hover:bg-gray-200 transition-colors" onClick={e => e.stopPropagation()} data-id="element-48">
                                        <MoreHorizontalIcon size={14} className="text-gray-400" data-id="element-49" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>)}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50" data-id="element-50">
                        <span className="text-xs text-gray-500" data-id="element-51">
                          {unreadNotifications.length} {t('app.AppLayout.of')}{' '}
                          {notifications.length}{' '}
                          {t('app.AppLayout.unread_notifications')}
                        </span>
                        <button onClick={() => {
                      setNotificationsOpen(false);
                      navigate('/notifications');
                    }} className="text-xs font-medium text-blue-600 hover:text-blue-700" data-id="element-52">
                          {t('app.AppLayout.view_all')}
                        </button>
                      </div>
                    </div>
                  </>}
              </div>

              {/* Create Menu */}
              <div className="relative group" data-id="element-53">
                <button type="button" onClick={() => setCreateMenuOpen(!createMenuOpen)} aria-label={t('actions.create') as string} aria-expanded={createMenuOpen} aria-haspopup="menu" className="p-1.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500" style={{
                backgroundColor: headerColors.createButton
              }} onMouseEnter={e => e.currentTarget.style.backgroundColor = headerColors.createButtonHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = headerColors.createButton} data-id="element-54">
                  <PlusIcon size={16} className="text-white" aria-hidden="true" data-id="element-55" />
                </button>
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" aria-hidden="true" data-id="element-56">
                  {t('actions.create')}
                </span>
                {createMenuOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setCreateMenuOpen(false)} data-id="element-57" />

                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20" data-id="element-58">
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/invoice-form');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-59">
                        <div className="flex items-center" data-id="element-60">
                          <FileTextIcon size={18} className="mr-3 text-gray-500" data-id="element-61" />

                          <span className="font-medium" data-id="element-62">
                            {t('app.AppLayout.invoice')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-63">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-64">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-65">
                            {t('app.AppLayout.i')}
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=recurring');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-66">
                        <div className="flex items-center" data-id="element-67">
                          <RepeatIcon size={18} className="mr-3 text-gray-500" data-id="element-68" />

                          <span className="font-medium" data-id="element-69">
                            {t('app.AppLayout.subscription')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-70">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-71">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-72">
                            {t('app.AppLayout.s')}
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=single');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-73">
                        <div className="flex items-center" data-id="element-74">
                          <LinkIcon size={18} className="mr-3 text-gray-500" data-id="element-75" />
                          <span className="font-medium" data-id="element-76">
                            {t('app.AppLayout.payment_link')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-77">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-78">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-79">
                            {t('app.AppLayout.l')}
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=charge');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-80">
                        <div className="flex items-center" data-id="element-81">
                          <CreditCardIcon size={18} className="mr-3 text-gray-500" data-id="element-82" />

                          <span className="font-medium" data-id="element-83">
                            {t('app.AppLayout.charge')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-84">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-85">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-86">
                            {t('app.AppLayout.p')}
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=security-hold');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-87">
                        <div className="flex items-center" data-id="element-88">
                          <ShieldIcon size={18} className="mr-3 text-gray-500" data-id="element-89" />

                          <span className="font-medium" data-id="element-90">
                            {t('app.AppLayout.security_hold')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-91">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-92">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-93">
                            {t('app.AppLayout.o')}
                          </kbd>
                        </div>
                      </button>
                    </div>
                  </>}
              </div>

              {/* User Menu */}
              <div className="relative ml-2" data-id="element-94">
                <button type="button" onClick={() => setUserMenuOpen(!userMenuOpen)} aria-label={`User menu, ${t('app.AppLayout.simon')}`} aria-expanded={userMenuOpen} aria-haspopup="menu" className="flex items-center hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-95">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/Screenshot_2026-03-17_at_06.40.16.png" alt={t('app.AppLayout.attrs.user_profile')} className="w-8 h-8 rounded-full object-cover" data-id="element-96" />

                  <span className="ml-2 hidden sm:block text-sm font-medium text-gray-700" data-id="element-97">
                    {t('app.AppLayout.simon')}
                  </span>
                  <ChevronDownIcon size={14} className={`ml-1 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} data-id="element-98" />
                </button>
                {userMenuOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} data-id="element-99" />

                    <div role="menu" aria-label="User menu" className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-100">
                      <button role="menuitem" onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=company');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-101">
                        <UsersIcon size={16} className="mr-3 text-gray-500" aria-hidden="true" data-id="element-102" />
                        {t('app.AppLayout.account')}
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=company');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-103">
                        <BarChartIcon size={16} className="mr-3 text-gray-500" data-id="element-104" />

                        {t('app.AppLayout.report')}
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-105">
                        <SettingsIcon size={16} className="mr-3 text-gray-500" data-id="element-106" />

                        {t('app.AppLayout.settings')}
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=security');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-107">
                        <ShieldIcon size={16} className="mr-3 text-gray-500" data-id="element-108" />
                        {t('app.AppLayout.security')}
                      </button>
                      <div className="border-t border-gray-100 my-1" data-id="element-109" />
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                    navigate('/');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-110">
                        <LogOutIcon size={16} className="mr-3" data-id="element-111" />
                        {t('app.AppLayout.logout')}
                      </button>
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar - sits below header, aligned with logo column */}
      <div id="primary-navigation" className={`
          fixed bottom-0 left-0 z-20 transform transition-transform duration-200 ease-in-out overflow-y-auto
          ${menuOpen ? 'translate-x-0 shadow-lg lg:shadow-none' : '-translate-x-full lg:translate-x-0'}
        `} style={{
      top: layout.header.height,
      width: layout.sidebar.width,
      backgroundColor: '#0066b3',
      borderRight: `1px solid ${sidebarColors.border}`
    }} data-id="element-112">
        <nav className="py-4 px-2" aria-label="Primary" data-id="element-113">
          {/* Dashboard */}
          <button onClick={() => navigate('/overview')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/overview'))} {...navItemHoverHandlers(isActive('/overview'))} data-id="element-114">
            <LayoutGridIcon size={16} className="mr-3 flex-shrink-0" data-id="element-115" />
            {t('app.AppLayout.dashboard')}
          </button>

          {/* Section: Merchants & Partners */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-116">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-117">
              {t('app.AppLayout.merchants_partners')}
            </span>
          </div>
          <button onClick={() => navigate('/payment-requests')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/payment-requests'))} {...navItemHoverHandlers(isActive('/payment-requests'))} data-id="element-118">
            <CreditCardIcon size={16} className="mr-3 flex-shrink-0" data-id="element-119" />
            {t('app.AppLayout.payment_requests')}
          </button>

          {/* QR Codes */}
          <button onClick={() => navigate('/qr-codes')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/qr-codes'))} {...navItemHoverHandlers(isActive('/qr-codes'))} data-id="element-120">
            <QrCodeIcon size={16} className="mr-3 flex-shrink-0" data-id="element-121" />
            QR Codes
          </button>

          {/* Terminals */}
          <button type="button" onClick={() => setTerminalsExpanded(!terminalsExpanded)} aria-expanded={terminalsExpanded} aria-controls="nav-terminals-submenu" className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals'))} {...navItemHoverHandlers(isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals'))} data-id="element-122">
            <div className="flex items-center" data-id="element-123">
              <TerminalIcon size={16} className="mr-3 flex-shrink-0" aria-hidden="true" data-id="element-124" />
              {t('app.AppLayout.terminals')}
            </div>
            <ChevronDownIcon size={14} aria-hidden="true" className={`transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-125" />
          </button>
          {terminalsExpanded && <div className="pl-7" id="nav-terminals-submenu" data-id="element-126">
              <button onClick={() => navigate('/virtual-terminal')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/virtual-terminal'))} {...subNavHoverHandlers(isActive('/virtual-terminal'))} data-id="element-127">
                {t('app.AppLayout.virtual')}
              </button>
              <button onClick={() => navigate('/physical-terminals')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/physical-terminals'))} {...subNavHoverHandlers(isActive('/physical-terminals'))} data-id="element-128">
                {t('app.AppLayout.physical')}
              </button>
              <button onClick={() => navigate('/softpos-terminals')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/softpos-terminals'))} {...subNavHoverHandlers(isActive('/softpos-terminals'))} data-id="element-129">
                {t('app.AppLayout.softpos')}
              </button>
            </div>}

          <button onClick={() => navigate('/invoices')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/invoices'))} {...navItemHoverHandlers(isActive('/invoices'))} data-id="element-130">
            <FileTextIcon size={16} className="mr-3 flex-shrink-0" data-id="element-131" />
            {t('app.AppLayout.invoices')}
          </button>
          <button onClick={() => navigate('/bills')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/'))} {...navItemHoverHandlers(isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/'))} data-id="element-132">
            <FileIcon size={16} className="mr-3 flex-shrink-0" data-id="element-133" />
            {t('app.AppLayout.bills')}
          </button>
          <button onClick={() => navigate('/reconciliation')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/reconciliation'))} {...navItemHoverHandlers(isActive('/reconciliation'))} data-id="element-134">
            <ArrowLeftRightIcon size={16} className="mr-3 flex-shrink-0" data-id="element-135" />
            {t('app.AppLayout.reconciliation')}
          </button>

          {/* Relationships */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-136">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-137">
              {t('app.AppLayout.relationships')}
            </span>
          </div>
          <button onClick={() => navigate('/customers')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/customers'))} {...navItemHoverHandlers(isActive('/customers'))} data-id="element-138">
            <UsersIcon size={16} className="mr-3 flex-shrink-0" data-id="element-139" />
            {t('app.AppLayout.customers')}
          </button>
          <button onClick={() => navigate('/suppliers')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form'))} {...navItemHoverHandlers(isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form'))} data-id="element-140">
            <TruckIcon size={16} className="mr-3 flex-shrink-0" data-id="element-141" />
            {t('app.AppLayout.suppliers')}
          </button>

          {/* Payments */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-142">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-143">
              {t('app.AppLayout.payments')}
            </span>
          </div>
          <button type="button" onClick={() => setBalanceExpanded(!balanceExpanded)} aria-expanded={balanceExpanded} aria-controls="nav-balance-submenu" className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isBalanceActive())} {...navItemHoverHandlers(isBalanceActive())} data-id="element-144">
            <div className="flex items-center" data-id="element-145">
              <BanknoteIcon size={16} className="mr-3 flex-shrink-0" aria-hidden="true" data-id="element-146" />
              {t('app.AppLayout.balance')}
            </div>
            <ChevronDownIcon size={14} aria-hidden="true" className={`transition-transform ${balanceExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-147" />
          </button>
          {balanceExpanded && <div className="pl-7" id="nav-balance-submenu" data-id="element-148">
              <button onClick={() => navigate('/balance')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/balance'))} {...subNavHoverHandlers(isActive('/balance'))} data-id="element-149">
                {t('app.AppLayout.summary')}
              </button>
              <button onClick={() => navigate('/balance-activity')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/balance-activity'))} {...subNavHoverHandlers(isActive('/balance-activity'))} data-id="element-150">
                {t('app.AppLayout.activity')}
              </button>
              <button onClick={() => navigate('/payouts')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/payouts'))} {...subNavHoverHandlers(isActive('/payouts'))} data-id="element-151">
                {t('app.AppLayout.payouts')}
              </button>
            </div>}
          <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isTransactionsActive())} {...navItemHoverHandlers(isTransactionsActive())} data-id="element-152">
            <div className="flex items-center" data-id="element-153">
              <ArrowLeftRightIcon size={16} className="mr-3 flex-shrink-0" data-id="element-154" />
              {t('app.AppLayout.transactions')}
            </div>
            <ChevronDownIcon size={14} className={`transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-155" />
          </button>
          {transactionsExpanded && <div className="pl-7" data-id="element-156">
              <button onClick={() => navigate('/payment-analytics')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/payment-analytics'))} {...subNavHoverHandlers(isActive('/payment-analytics'))} data-id="element-157">
                {t('app.AppLayout.payment_analytics')}
              </button>
              <button onClick={() => navigate('/sales')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/sales') || location.pathname.startsWith('/sale-detail/'))} {...subNavHoverHandlers(isActive('/sales') || location.pathname.startsWith('/sale-detail/'))} data-id="element-158">
                {t('app.AppLayout.sales')}
              </button>
              <button onClick={() => navigate('/disputes')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/disputes') || location.pathname.startsWith('/dispute-detail/'))} {...subNavHoverHandlers(isActive('/disputes') || location.pathname.startsWith('/dispute-detail/'))} data-id="element-159">
                {t('app.AppLayout.disputes')}
              </button>
              <button onClick={() => navigate('/refunds')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/refunds') || location.pathname.startsWith('/refund-detail/'))} {...subNavHoverHandlers(isActive('/refunds') || location.pathname.startsWith('/refund-detail/'))} data-id="element-160">
                {t('app.AppLayout.refunds')}
              </button>
            </div>}

          {/* E-Commerce */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-161">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-162">
              {t('app.AppLayout.ecommerce')}
            </span>
          </div>
          <button onClick={() => navigate('/shifts')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/shifts') || location.pathname.startsWith('/shifts/'))} {...navItemHoverHandlers(isActive('/shifts') || location.pathname.startsWith('/shifts/'))} data-id="element-163">
            <ClockIcon size={16} className="mr-3 flex-shrink-0" data-id="element-164" />
            Shifts
          </button>
          <button onClick={() => navigate('/shops')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/shops'))} {...navItemHoverHandlers(isActive('/shops'))} data-id="element-165">
            <ShoppingBagIcon size={16} className="mr-3 flex-shrink-0" data-id="element-166" />
            {t('app.AppLayout.shops')}
          </button>
          <button onClick={() => navigate('/orders')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/orders'))} {...navItemHoverHandlers(isActive('/orders'))} data-id="element-167">
            <ShoppingCartIcon size={16} className="mr-3 flex-shrink-0" data-id="element-168" />
            {t('app.AppLayout.orders')}
          </button>
          <button onClick={() => navigate('/campaigns')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form'))} {...navItemHoverHandlers(isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form'))} data-id="element-169">
            <MegaphoneIcon size={16} className="mr-3 flex-shrink-0" data-id="element-170" />
            {t('app.AppLayout.campaigns')}
          </button>

          {/* Reports & Analytics */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-171">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-172">
              {t('app.AppLayout.reports_analytics')}
            </span>
          </div>
          <button onClick={() => setReportingExpanded(!reportingExpanded)} className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isReportingActive())} {...navItemHoverHandlers(isReportingActive())} data-id="element-173">
            <div className="flex items-center" data-id="element-174">
              <BarChartIcon size={16} className="mr-3 flex-shrink-0" data-id="element-175" />
              {t('app.AppLayout.reporting')}
            </div>
            <ChevronDownIcon size={14} className={`transition-transform ${reportingExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-176" />
          </button>
          {reportingExpanded && <div className="pl-7" data-id="element-177">
              <button onClick={() => navigate('/reports')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/reports'))} {...subNavHoverHandlers(isActive('/reports'))} data-id="element-178">
                {t('app.AppLayout.reports')}
              </button>
              <button onClick={() => navigate('/custom-metrics')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/custom-metrics'))} {...subNavHoverHandlers(isActive('/custom-metrics'))} data-id="element-179">
                {t('app.AppLayout.custom_metrics')}
              </button>
              <button onClick={() => navigate('/nadi')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/nadi'))} {...subNavHoverHandlers(isActive('/nadi'))} data-id="element-180">
                {t('app.AppLayout.nadi')}
              </button>
            </div>}

          {/* Developers */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-181">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-182">
              Developers
            </span>
          </div>
          <button onClick={() => navigate('/connect')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/connect'))} {...navItemHoverHandlers(isActive('/connect'))} data-id="element-183">
            <LinkIcon size={16} className="mr-3 flex-shrink-0" data-id="element-184" />
            Connect
          </button>
          <button onClick={() => setWebhooksExpanded(!webhooksExpanded)} className="w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/webhooks') || isActive('/webhooks/endpoints') || isActive('/webhooks/logs'))} {...navItemHoverHandlers(isActive('/webhooks') || isActive('/webhooks/endpoints') || isActive('/webhooks/logs'))} data-id="element-185">
            <div className="flex items-center" data-id="element-186">
              <WebhookIcon size={16} className="mr-3 flex-shrink-0" data-id="element-187" />
              Webhooks
            </div>
            <ChevronDownIcon size={14} className={`transition-transform ${webhooksExpanded ? 'rotate-180' : ''}`} style={{
            color: sidebarColors.chevron
          }} data-id="element-188" />
          </button>
          {webhooksExpanded && <div className="pl-7" data-id="element-189">
              <button onClick={() => navigate('/webhooks/endpoints')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/webhooks/endpoints'))} {...subNavHoverHandlers(isActive('/webhooks/endpoints'))} data-id="element-190">
                Endpoints
              </button>
              <button onClick={() => navigate('/webhooks/logs')} className="w-full flex items-center px-3 py-1.5 text-[13px] transition-colors" style={subNavItemStyle(isActive('/webhooks/logs'))} {...subNavHoverHandlers(isActive('/webhooks/logs'))} data-id="element-191">
                Logs
              </button>
            </div>}
          {/* System */}
          <div className="mt-4 mb-1 px-4" data-id="element-192">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{
            color: sidebarColors.text.sectionHeader
          }} data-id="element-193">
              {t('app.AppLayout.system')}
            </span>
          </div>
          <button onClick={() => navigate('/activity-logs')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/activity-logs'))} {...navItemHoverHandlers(isActive('/activity-logs'))} data-id="element-194">
            <ActivityIcon size={16} className="mr-3 flex-shrink-0" data-id="element-195" />
            Activity Logs
          </button>
          <button onClick={() => navigate('/requests')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/requests'))} {...navItemHoverHandlers(isActive('/requests'))} data-id="element-196">
            <ClipboardListIcon size={16} className="mr-3 flex-shrink-0" data-id="element-197" />
            Requests
          </button>
          <button onClick={() => navigate('/settings')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/settings'))} {...navItemHoverHandlers(isActive('/settings'))} data-id="element-198">
            <SettingsIcon size={16} className="mr-3 flex-shrink-0" data-id="element-199" />
            {t('app.AppLayout.settings')}
          </button>
          <button onClick={() => navigate('/tutorials')} className="w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors" style={navItemStyle(isActive('/tutorials'))} {...navItemHoverHandlers(isActive('/tutorials'))} data-id="element-200">
            <GraduationCapIcon size={16} className="mr-3 flex-shrink-0" data-id="element-201" />
            {t('app.AppLayout.tutorials')}
          </button>
          <div className="h-6" data-id="element-202" />
        </nav>
      </div>

      {/* Main content */}
      <main id="main-content" className="lg:pl-60 min-h-screen" style={{
      paddingTop: layout.header.height
    }} data-id="element-203">
        <div className="px-6 lg:px-8 py-6" data-id="element-204">{children}</div>
      </main>

      {/* AI Assistant Panel */}
      {assistantOpen && <>
          <div className="fixed inset-0 z-40 transition-opacity" style={{
        backgroundColor: colors.background.overlay
      }} onClick={() => setAssistantOpen(false)} data-id="element-205" />

          <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col" data-id="element-206">
            <div className="flex items-center justify-between p-4" style={{
          borderBottom: `1px solid ${headerColors.border}`
        }} data-id="element-207">
              <div className="flex items-center gap-2" data-id="element-208">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
              backgroundColor: brand.primary
            }} data-id="element-209">
                  <span className="text-white text-sm font-semibold" data-id="element-210">✨</span>
                </div>
                <h2 className="text-lg font-semibold" style={{
              color: colors.text.primary
            }} data-id="element-211">
                  {t('app.AppLayout.assistant')}
                </h2>
              </div>
              <button onClick={() => setAssistantOpen(false)} className="p-1 rounded transition-colors hover:bg-gray-100" data-id="element-212">
                <XIcon size={20} style={{
              color: headerColors.iconDefault
            }} data-id="element-213" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-214">
              <div className="space-y-4" data-id="element-215">
                <div className="bg-gray-50 rounded-lg p-4" data-id="element-216">
                  <p className="text-sm text-gray-700 mb-2" data-id="element-217">
                    {t('app.AppLayout.hi_im_pomelo_pay_assistant')}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-218">
                    {t('app.AppLayout.what_do_you_need_help_with')}
                  </p>
                </div>
                <div className="space-y-2" data-id="element-219">
                  {['Contact merchant support', 'My verification status', 'Change business type', 'My settlement is missing', 'Update bank account for settlement', 'Malaysia payment regulations'].map(label => <button key={label} className="w-full text-left px-4 py-3 rounded-lg transition-colors" style={{
                border: `1px solid ${headerColors.border}`
              }} onMouseEnter={e => {
                e.currentTarget.style.borderColor = assistantColors.optionBorderHover;
                e.currentTarget.style.backgroundColor = assistantColors.optionBgHover;
              }} onMouseLeave={e => {
                e.currentTarget.style.borderColor = headerColors.border;
                e.currentTarget.style.backgroundColor = 'transparent';
              }} data-id="element-220">
                      <p className="text-sm font-medium" style={{
                  color: colors.text.primary
                }} data-id="element-221">
                        {label}
                      </p>
                    </button>)}
                </div>
                <div className="pt-4" style={{
              borderTop: `1px solid ${headerColors.border}`
            }} data-id="element-222">
                  <p className="text-xs font-medium mb-2" style={{
                color: colors.text.tertiary
              }} data-id="element-223">
                    {t('app.AppLayout.recent')}
                  </p>
                  <button className="w-full text-left px-4 py-3 rounded-lg transition-colors" style={{
                border: `1px solid ${headerColors.border}`
              }} onMouseEnter={e => {
                e.currentTarget.style.borderColor = assistantColors.optionBorderHover;
                e.currentTarget.style.backgroundColor = assistantColors.optionBgHover;
              }} onMouseLeave={e => {
                e.currentTarget.style.borderColor = headerColors.border;
                e.currentTarget.style.backgroundColor = 'transparent';
              }} data-id="element-224">
                    <p className="text-sm" style={{
                  color: colors.neutral[700]
                }} data-id="element-225">
                      {t('app.AppLayout.how_do_i_enable_fpx_for')}
                    </p>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4" style={{
          borderTop: `1px solid ${headerColors.border}`
        }} data-id="element-226">
              <div className="relative" data-id="element-227">
                <input type="text" placeholder={placeholderTexts[placeholderIndex]} className="w-full px-4 py-3 pr-10 rounded-lg outline-none transition-all" style={{
              border: `1px solid ${colors.border.DEFAULT}`
            }} onFocus={e => {
              e.target.style.borderColor = assistantColors.inputFocusRing;
              e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
            }} onBlur={e => {
              e.target.style.borderColor = colors.border.DEFAULT;
              e.target.style.boxShadow = 'none';
            }} key={placeholderIndex} data-id="element-228" />

                <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 transition-colors" style={{
              color: colors.neutral[400]
            }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[600]} onMouseLeave={e => e.currentTarget.style.color = colors.neutral[400]} data-id="element-229">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" data-id="element-230">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" data-id="element-231" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 z-10 lg:hidden" style={{
      backgroundColor: colors.background.overlay
    }} onClick={() => setMenuOpen(false)} data-id="element-232"></div>}
    </div>;
}